//core
import React, {useEffect} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Box,
  Typography,
  Button,
} from "@mui/material";
import { ArrowForward } from "@mui/icons-material";
import { getSymbolFromCode } from "currency-code-symbol-map";
import { useSelector } from "react-redux";

//styles
import { useStyles } from "./styles";

//routes
import routes from "../../../../../../../../../navigation/routes";

//hooks
import { useConfiguration } from "../../../../../../../../../store/common/configuration/useConfiguration";
import { usePermissions } from "../../../../../../../../../store/common/configuration/usePermissions";

//selectors
import { selectUploadConfigData } from "../../../../../../../../../store/common/configuration/selectors";

//components
import CardMenu from "../CardMenu";
import { listItem } from "../../../../../../../../../store/admin/flatFee";
import Status from "../../../../../../../../common/TableData/components/Status";
import FloatFormat from "../../../../../../../../common/TableData/components/FloatFormat";
import ClickableUserId from "../../../../../../../../common/TableData/components/ClickableUserId";
import {normalizeNumber} from "../../../../../../../../../helpers/utils";
import Text from "../../../../../../../../common/TableData/components/Text";

interface IItemCardProps {
  data: listItem;
	columns: any;
}

const ItemCard: React.FC<IItemCardProps> = ({
  data,
  columns,
}): JSX.Element => {
  const styles = useStyles();
  const { t } = useTranslation();
  let history = useNavigate();
	const { getUploadConf } = useConfiguration();
	const { hasPermissions } = usePermissions();

	const uploadConfigData = useSelector(selectUploadConfigData);

	const hasDelete = hasPermissions(["api3.admin.flatfeecontroller.annulate"]);
	const hasUpdate = hasPermissions(["api3.admin.flatfeecontroller.edit"]);

  const handleDetails = (id: string | number) => {
    history(routes.admin.flatFee.details.replace(':flatFeeId', `${id}`));
  }

	useEffect(() => {
		if (!uploadConfigData) {
			getUploadConf();
		}
	}, [uploadConfigData]);

  return (
    <Card className={styles.card} elevation={1}>
	    <Box className={styles.top}>
		    <Typography className={styles.id} variant="caption" component="div">{`ID ${data.id}`}</Typography>
		    {
			    (hasDelete || hasUpdate) && (
				    <CardMenu data={data} />
			    )
		    }
	    </Box>
	    <Box className={styles.field}>
		    <Typography className={styles.label} variant="body2" component="span">
			    {t("columns_for_display.table.flatFee.columns.status")}:
		    </Typography>
		    <Typography variant="body2" component="span">
			    <Status value={data.status}/>
		    </Typography>
	    </Box>
      <Box className={styles.field}>
        <Typography className={styles.label} variant="body2" component="span">
	        {t(`columns_for_display.table.flatFee.columns.amount`).replace('{currency}', !!uploadConfigData ? getSymbolFromCode(uploadConfigData.defaultCurrency.toUpperCase()) || '$' : '$')}:
        </Typography>
        <Typography variant="body2" component="span"><Text value={normalizeNumber(data.amount)} whiteSpace /></Typography>
      </Box>
	    <Box className={styles.field}>
        <Typography className={styles.label} variant="body2" component="span">
          {t("columns_for_display.table.flatFee.columns.affiliate_id")}:
        </Typography>
        <Typography variant="body2" component="span"><ClickableUserId value={data.affiliate_id} /></Typography>
      </Box>
	    <Box className={styles.field}>
        <Typography className={styles.label} variant="body2" component="span">
          {t("columns_for_display.table.flatFee.columns.tracker_id")}:
        </Typography>
        <Typography variant="body2" component="span">{data.tracker_id}</Typography>
      </Box>

      <Box className={styles.actions}>
        <Button
          className={styles.button}
          color="primary"
          size="small"
          variant="outlined"
          endIcon={<ArrowForward />}
          onClick={() => handleDetails(data.id)}
        >
          {t("common.buttons.view_all_info")}
        </Button>
      </Box>
    </Card>
  );
};

export default ItemCard;
